import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { formatCurrency, getPerformanceClass } from "@/utils/formatters";
import { formatDistanceToNow } from "date-fns";
import CreateAccountModal from "../CreateAccountModal";
import api from "@/api";

// --- Logo fallback helpers ---

const getInitials = (label) => {
  if (!label) return "?";
  return (
    String(label)
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() || "")
      .join("") || "?"
  );
};

const getInitialsBadgeDataUri = (label) => {
  const initials = getInitials(label);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40"><rect width="40" height="40" rx="8" fill="#E5E7EB"/><text x="20" y="25" text-anchor="middle" font-family="Arial, sans-serif" font-size="14" font-weight="700" fill="#374151">${initials}</text></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

// --- Component ---

const AccountsSidebar = ({
  accounts,
  selectedAccountId,
  activeTab,
  onAccountCreated,
  onRefresh,
  refreshingAccountId,
  refreshTrigger = 0,
  onSyncComplete,
}) => {
  const location = useLocation();
  const isIntelPage = location.pathname === "/intel";
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [period, setPeriod] = useState(() => {
    return localStorage.getItem("selectedPeriod") || "daily";
  });
  const [netWorthData, setNetWorthData] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    localStorage.setItem("selectedPeriod", period);
  }, [period]);

  const accountsKey = useMemo(() => {
    return accounts
      .map((a) => a.id)
      .sort()
      .join(",");
  }, [accounts]);

  const fetchNetWorthData = useCallback(async () => {
    if (!accounts || accounts.length === 0) {
      setNetWorthData({
        total_net_worth: 0,
        period_change: 0,
        period_change_percent: 0,
        period_label:
          period === "yearly"
            ? "last 365 days"
            : period === "daily"
              ? "today"
              : `this ${period}`,
        period: period,
        accounts: [],
      });
      setInitialLoading(false);
      return;
    }

    try {
      const response = await api.get(
        `/accounts/summary/net-worth?period=${period}`,
      );
      setNetWorthData(response.data);
    } catch (error) {
      console.error("Error fetching net worth data:", error);
      const totalValue = accounts.reduce(
        (sum, account) =>
          sum + (account.total_account_value || account.total_value || 0),
        0,
      );
      setNetWorthData({
        total_net_worth: totalValue,
        period_change: 0,
        period_label: "last 365 days",
        accounts: accounts.map((acc) => ({
          id: acc.id,
          name: acc.name,
          account_type: acc.account_type,
          current_value: acc.total_account_value || acc.total_value || 0,
          period_change: 0,
          last_sync: acc.snaptrade_last_synced_at,
          source: acc.source,
          sync_in_progress: acc.sync_in_progress,
          sync_status: acc.sync_status,
          account_mask: acc.snaptrade_account_mask,
          brokerage_name: acc.snaptrade_brokerage,
          brokerage_logo: null,
        })),
      });
    } finally {
      setInitialLoading(false);
    }
  }, [period, accounts]);

  useEffect(() => {
    fetchNetWorthData();
  }, [fetchNetWorthData, accountsKey, refreshTrigger]);

  const wasSyncingRef = React.useRef(false);
  useEffect(() => {
    const hasSyncInProgress = netWorthData?.accounts?.some(
      (a) => a.sync_in_progress,
    );

    if (wasSyncingRef.current && !hasSyncInProgress) {
      wasSyncingRef.current = false;
      if (onSyncComplete) onSyncComplete();
      return;
    }

    wasSyncingRef.current = hasSyncInProgress;
    if (!hasSyncInProgress) return;

    const interval = setInterval(fetchNetWorthData, 3000);
    return () => clearInterval(interval);
  }, [netWorthData, fetchNetWorthData, onSyncComplete]);

  const allAccountsUrl =
    activeTab && activeTab !== "summary" ? `/${activeTab}` : "/";

  const getAccountUrl = (accountId) => {
    if (activeTab && activeTab !== "summary") {
      return `/account/${accountId}/${activeTab}`;
    }
    return `/account/${accountId}`;
  };

  if (initialLoading || !netWorthData) {
    return (
      <div className="w-64 bg-gray-50 dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 h-full overflow-y-auto flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="w-64 bg-gray-50 dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 h-full overflow-y-auto">
      <div className="p-4">
        {/* Net Worth Summary */}
        <Link
          to={allAccountsUrl}
          className={`block rounded-lg p-4 mb-4 transition-colors ${
            selectedAccountId === null && !isIntelPage
              ? "bg-blue-50 dark:bg-blue-900/30 border-2 border-blue-500"
              : "bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500"
          }`}
        >
          <div className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
            Net Worth
          </div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {formatCurrency(netWorthData.total_net_worth)}
          </div>

          <div
            className={`flex items-center gap-1 text-sm font-medium mb-3 ${getPerformanceClass(
              netWorthData.period_change,
            )}`}
          >
            <span>{netWorthData.period_change >= 0 ? "↗" : "↘"}</span>
            <span>{formatCurrency(Math.abs(netWorthData.period_change))}</span>
            <span className="text-xs font-normal">
              {netWorthData.period_label}
            </span>
          </div>

          {/* Period Toggle */}
          <div className="flex gap-1" onClick={(e) => e.preventDefault()}>
            {["daily", "weekly", "monthly", "yearly"].map((p, i) => (
              <button
                key={p}
                onClick={(e) => {
                  e.preventDefault();
                  setPeriod(p);
                }}
                className={`flex-1 py-1.5 px-2 text-xs font-medium rounded transition-colors ${
                  period === p
                    ? "bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300"
                    : "bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-500"
                }`}
              >
                {["D", "W", "M", "Y"][i]}
              </button>
            ))}
          </div>
        </Link>

        {/* Individual Accounts */}
        <div className="space-y-2">
          {netWorthData.accounts.map((accountData) => {
            const brokerLabel = accountData.brokerage_name || accountData.name || "Account";
            const logoSrc = accountData.brokerage_logo || null;

            return (
              <Link
                key={accountData.id}
                to={getAccountUrl(accountData.id)}
                className={`block w-full text-left p-3 rounded-lg transition-colors ${
                  selectedAccountId === accountData.id && !isIntelPage
                    ? "bg-blue-50 dark:bg-blue-900/30 border-2 border-blue-500"
                    : "bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white min-w-0">
                    {logoSrc ? (
                      <img
                        src={logoSrc}
                        alt={brokerLabel}
                        className="w-5 h-5 rounded flex-shrink-0"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = getInitialsBadgeDataUri(brokerLabel);
                        }}
                      />
                    ) : (
                      <div className="w-5 h-5 rounded bg-gray-200 dark:bg-gray-600 text-[10px] font-bold text-gray-700 dark:text-gray-200 flex items-center justify-center flex-shrink-0">
                        {getInitials(brokerLabel)}
                      </div>
                    )}
                    <div className="truncate">
                      {accountData.name}
                      {accountData.account_mask && (
                        <span className="text-gray-500 dark:text-gray-400 font-normal ml-1">
                          ({accountData.account_mask})
                        </span>
                      )}
                    </div>
                  </div>
                  {(accountData.sync_in_progress ||
                    refreshingAccountId === accountData.id) && (
                    <svg
                      className="animate-spin h-3 w-3 text-blue-600 dark:text-blue-400 flex-shrink-0"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  )}
                  {!accountData.sync_in_progress &&
                    accountData.sync_status
                      ?.toLowerCase()
                      .includes("failed") && (
                      <svg
                        title={accountData.sync_status}
                        className="h-3 w-3 text-red-500 dark:text-red-400 flex-shrink-0"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                </div>

                <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  {accountData.brokerage_name && (
                    <span className="font-medium">
                      {accountData.brokerage_name}
                      {accountData.account_type && " • "}
                    </span>
                  )}
                  {accountData.account_type}
                  {accountData.last_sync && (
                    <span className="ml-2">
                      •{" "}
                      {formatDistanceToNow(new Date(accountData.last_sync), {
                        addSuffix: true,
                      })}
                    </span>
                  )}
                </div>

                <div className="text-base font-bold text-gray-900 dark:text-white mb-1">
                  {formatCurrency(accountData.current_value)}
                </div>

                <div
                  className={`text-sm font-medium ${getPerformanceClass(
                    accountData.period_change,
                  )}`}
                >
                  {accountData.period_change > 0 ? "+" : ""}
                  {formatCurrency(accountData.period_change)}
                </div>
              </Link>
            );
          })}
        </div>

        {/* Add Account Button */}
        <button
          onClick={() => setShowCreateModal(true)}
          className="w-full mt-3 p-3 rounded-lg bg-white dark:bg-gray-700 border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-blue-500 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 font-medium text-sm"
        >
          + Add Account
        </button>
      </div>

      {showCreateModal && (
        <CreateAccountModal
          onClose={() => setShowCreateModal(false)}
          onCreate={onAccountCreated}
          onRefresh={onRefresh}
        />
      )}
    </div>
  );
};

export default AccountsSidebar;
